import { Box, Card, Flex, Text, ThemeIcon } from '@mantine/core';
import React from 'react';
import { ReactNode } from 'react';

/**
 * Props for the StatCard KPI display component.
 */
export interface StatCardProps {
  /** Small uppercase label above the value */
  label: ReactNode;
  /** Primary display value */
  value: ReactNode;
  /** Tabler icon element shown inside a ThemeIcon */
  icon: ReactNode;
  /** Mantine color key for the icon badge (e.g. 'blue', 'teal') */
  color: string;
  /** Optional secondary line below the value */
  sub?: ReactNode;
  /** When true, value and sub are visually blurred */
  blurred?: boolean;
  /** When provided, the card becomes clickable (cursor + hover affordance) and invokes this on click */
  onClick?: () => void;
  /** When true, disables click interaction and dims the card (e.g. zero-count drill-down) */
  disabled?: boolean;
}

/**
 * A statistics display card showing a label, primary value with optional secondary text, and an icon badge, with support for blurred states.
 */
export function StatCard({ label, value, icon, color, sub, blurred, onClick, disabled }: StatCardProps) {
  const clickable = !!onClick && !disabled;
  return (
    <Card
      withBorder
      radius='md'
      p='md'
      onClick={clickable ? onClick : undefined}
      data-clickable={clickable || undefined}
      style={{
        minWidth: 0,
        cursor: clickable ? 'pointer' : undefined,
        opacity: disabled ? 0.55 : undefined,
        transition: 'border-color 120ms ease, box-shadow 120ms ease, transform 120ms ease',
      }}
      onMouseEnter={clickable ? (e) => {
        e.currentTarget.style.borderColor = `var(--mantine-color-${color}-5)`;
        e.currentTarget.style.boxShadow = 'var(--mantine-shadow-sm)';
      } : undefined}
      onMouseLeave={clickable ? (e) => {
        e.currentTarget.style.borderColor = '';
        e.currentTarget.style.boxShadow = '';
      } : undefined}
    >
      <Flex align='flex-start' justify='space-between' gap='xs'>
        <Box style={{ minWidth: 0, flex: 1 }}>
          <Text fz='xs' c='dimmed' fw={500} tt='uppercase' style={{ letterSpacing: '0.06em' }} mb={4}>
            {label}
          </Text>
          <Text
            fz='xl'
            fw={700}
            truncate
            style={{ lineHeight: 1.2, ...(blurred ? { filter: 'blur(6px)', userSelect: 'none' } : {}) }}
          >
            {value}
          </Text>
          {sub && (
            <Text fz='xs' c='dimmed' mt={4} style={blurred ? { filter: 'blur(4px)', userSelect: 'none' } : {}}>
              {sub}
            </Text>
          )}
        </Box>
        <ThemeIcon size='lg' variant='light' color={color} radius='md' style={{ flexShrink: 0 }}>
          {icon}
        </ThemeIcon>
      </Flex>
    </Card>
  );
}
